"use client";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, Plus, Minus, Pencil, Check, X, Hash } from "lucide-react";

export default function SaleItems({
  items = [],
  onUpdateQuantity,
  onRemoveItem,
  onUpdatePrice,
  // Whether this user may override a line's price. Defaults to false so any
  // caller that forgets to pass it gets the safe, locked behaviour.
  canEditPrice = false,
  onUpdateImei,
  onUpdateUnit,
}) {
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [tempPrice, setTempPrice] = useState("");
  const [priceError, setPriceError] = useState("");

  if (!Array.isArray(items)) {
    return null;
  }

  const startEditPrice = (productId, currentPrice) => {
    setEditingPriceId(productId);
    setTempPrice(currentPrice.toString());
    setPriceError("");
  };

  const cancelEditPrice = () => {
    setEditingPriceId(null);
    setTempPrice("");
    setPriceError("");
  };

  const savePrice = (productId, minPrice) => {
    const newPrice = parseFloat(tempPrice);

    if (isNaN(newPrice) || newPrice <= 0) {
      setPriceError("Invalid price");
      return;
    }

    if (newPrice < minPrice) {
      setPriceError(`Cannot be below GHS ${minPrice.toFixed(2)}`);
      return;
    }

    if (onUpdatePrice && productId) {
      onUpdatePrice(productId, newPrice);
    }

    setEditingPriceId(null);
    setTempPrice("");
    setPriceError("");
  };

  return (
    <div className="border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>IMEI/Serial</TableHead>
            <TableHead>Subtotal</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const product = item?.product || {};
            const quantity = item?.quantity || 0;
            const price = item?.price || 0;
            const imei = item?.imei || "";
            const minPrice = parseFloat(product?.current_price || 0);
            const isEditing = editingPriceId === product?.id;

            return (
              <TableRow key={product?.id}>
                <TableCell>
                  <div>
                    <span>{product?.title || "Unknown Product"}</span>
                    <p className="text-xs text-muted-foreground">
                      Retail: GHS {minPrice.toFixed(2)}
                    </p>
                    {Array.isArray(product?.units) && product.units.length > 0 && onUpdateUnit && (
                      <select
                        value={item?.unit_id ?? ""}
                        onChange={(e) => onUpdateUnit(product.id, e.target.value)}
                        className="mt-1 text-xs border rounded px-1 py-0.5 bg-background"
                      >
                        {product.units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || (u.is_base ? "Base" : `x${u.factor_to_base}`)}
                            {!u.is_base ? ` (=${u.factor_to_base})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          min={minPrice}
                          value={tempPrice}
                          onChange={(e) => {
                            setTempPrice(e.target.value);
                            setPriceError("");
                          }}
                          className="w-24 h-8"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") savePrice(product.id, minPrice);
                            if (e.key === "Escape") cancelEditPrice();
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => savePrice(product.id, minPrice)}
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={cancelEditPrice}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      {priceError && (
                        <p className="text-xs text-red-500">{priceError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className={price > minPrice ? "text-blue-600 font-medium" : ""}>
                        GHS {(price || 0).toFixed(2)}
                      </span>
                      {onUpdatePrice && canEditPrice && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => startEditPrice(product.id, price)}
                          title="Edit price"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (onUpdateQuantity && product?.id) {
                          onUpdateQuantity(product.id, quantity - 1);
                        }
                      }}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value, 10);
                        if (
                          !isNaN(newQuantity) &&
                          onUpdateQuantity &&
                          product?.id
                        ) {
                          onUpdateQuantity(product.id, newQuantity);
                        }
                      }}
                      className="w-20 text-center"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (onUpdateQuantity && product?.id) {
                          onUpdateQuantity(product.id, quantity + 1);
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    type="text"
                    placeholder="IMEI/Serial..."
                    value={imei}
                    onChange={(e) => {
                      if (onUpdateImei && product?.id) {
                        onUpdateImei(product.id, e.target.value);
                      }
                    }}
                    className="w-32 h-8 text-xs"
                  />
                </TableCell>
                <TableCell>GHS {(price * quantity).toFixed(2)}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (onRemoveItem && product?.id) {
                        onRemoveItem(product.id);
                      }
                    }}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">
                No items added. Use the search above to add products.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
